import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type FullMessage } from '../lib/api';
import { sanitizeMailHtml } from '../lib/sanitize';

export function MessageDetailPage() {
  const { accountId, mailbox: encodedMailbox, uid } = useParams<{
    accountId: string;
    mailbox: string;
    uid: string;
  }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const mailboxPath = encodedMailbox ? decodeURIComponent(encodedMailbox) : '';
  const [showImages, setShowImages] = useState(false);
  const [showHtml, setShowHtml] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['message', accountId, mailboxPath, uid],
    queryFn: () =>
      api.get<{ message: FullMessage }>(
        `/accounts/${accountId}/mailboxes/${encodeURIComponent(mailboxPath)}/messages/${uid}`,
      ),
    enabled: !!accountId && !!mailboxPath && !!uid,
  });

  const sanitizedHtml = useMemo(() => {
    if (!data?.message.html) return '';
    return sanitizeMailHtml(data.message.html, { showImages });
  }, [data, showImages]);

  useEffect(() => {
    if (!iframeRef.current || !showHtml || !sanitizedHtml) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8">
      <base target="_blank">
      <style>
        body { font-family: ui-sans-serif, system-ui, sans-serif; color: #1e293b;
               margin: 8px; word-wrap: break-word; overflow-wrap: anywhere; }
        img { max-width: 100%; height: auto; }
        .rla-blocked-image { background: #f1f5f9; border: 1px dashed #cbd5e1; padding: 8px; }
        a { color: #0a4ea6; text-decoration: underline; }
        blockquote { border-left: 3px solid #cbd5e1; padding-left: 8px; color: #475569; margin-left: 0; }
        table { border-collapse: collapse; max-width: 100%; }
      </style>
      </head><body>${sanitizedHtml}</body></html>`);
    doc.close();
  }, [sanitizedHtml, showHtml]);

  const del = useMutation({
    mutationFn: () =>
      api.delete(
        `/accounts/${accountId}/mailboxes/${encodeURIComponent(mailboxPath)}/messages/${uid}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messages', accountId, mailboxPath] });
      qc.invalidateQueries({ queryKey: ['mailboxes', accountId] });
      nav(`/account/${accountId}/inbox/${encodedMailbox}`);
    },
  });

  const toggleRead = useMutation({
    mutationFn: (read: boolean) =>
      api.patch(
        `/accounts/${accountId}/mailboxes/${encodeURIComponent(mailboxPath)}/messages/${uid}`,
        { read },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['message', accountId, mailboxPath, uid] });
      qc.invalidateQueries({ queryKey: ['messages', accountId, mailboxPath] });
      qc.invalidateQueries({ queryKey: ['mailboxes', accountId] });
    },
  });

  if (isLoading) return <div className="text-slate-500">Lade Nachricht …</div>;
  if (error)
    return (
      <div className="card bg-red-50 border-red-200 text-red-700">
        {error instanceof ApiError ? error.message : 'Fehler beim Laden'}
      </div>
    );
  if (!data) return null;

  const m = data.message;
  const hasHtml = !!m.html;
  const hasText = !!m.text;
  const fromDomain = m.fromAddress.split('@').pop() ?? '';
  const refSpec = `${mailboxPath}/${uid}`;

  return (
    <div>
      <div className="mb-3 flex items-center gap-2 flex-wrap text-sm">
        <Link
          to={`/account/${accountId}/inbox/${encodedMailbox}`}
          className="text-rla-700 hover:underline"
        >
          ← Zurück
        </Link>
        <Link
          to={`/account/${accountId}/compose?reply=${encodeURIComponent(refSpec)}`}
          className="btn-secondary text-sm"
        >
          ↩ Antworten
        </Link>
        <Link
          to={`/account/${accountId}/compose?replyAll=${encodeURIComponent(refSpec)}`}
          className="btn-secondary text-sm"
        >
          ↩↩ Allen
        </Link>
        <Link
          to={`/account/${accountId}/compose?forward=${encodeURIComponent(refSpec)}`}
          className="btn-secondary text-sm"
        >
          ➡ Weiterleiten
        </Link>
        <button
          onClick={() => toggleRead.mutate(m.flags.includes('\\Seen') ? false : true)}
          className="btn-secondary text-sm ml-auto"
        >
          {m.flags.includes('\\Seen') ? 'Als ungelesen' : 'Als gelesen'}
        </button>
        <button
          onClick={() => {
            if (confirm('Mail in den Papierkorb verschieben?')) del.mutate();
          }}
          className="btn-danger text-sm"
        >
          🗑 Löschen
        </button>
      </div>

      <div className="card mb-4">
        <h1 className="text-xl font-semibold text-slate-800 mb-3 break-words">{m.subject}</h1>
        <div className="grid grid-cols-1 sm:grid-cols-[80px_1fr] gap-x-3 gap-y-1 text-sm">
          <div className="text-slate-500">Von:</div>
          <div className="break-all">
            <span className="font-medium">{m.fromName || m.fromAddress}</span>
            {m.fromName && <span className="text-slate-500"> &lt;{m.fromAddress}&gt;</span>}
            <span
              className="ml-2 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded"
              title="Domain des Absenders — bei Phishing-Verdacht prüfen"
            >
              @{fromDomain}
            </span>
          </div>
          {m.toAddresses.length > 0 && (
            <>
              <div className="text-slate-500">An:</div>
              <div className="break-all text-slate-700">
                {m.toAddresses.map((a) => a.address).join(', ')}
              </div>
            </>
          )}
          {m.ccAddresses.length > 0 && (
            <>
              <div className="text-slate-500">Kopie:</div>
              <div className="break-all text-slate-700">
                {m.ccAddresses.map((a) => a.address).join(', ')}
              </div>
            </>
          )}
          <div className="text-slate-500">Datum:</div>
          <div className="text-slate-700">{new Date(m.date).toLocaleString('de-DE')}</div>
        </div>
      </div>

      {hasHtml && hasText && (
        <div className="flex gap-2 mb-2 text-sm">
          <button
            onClick={() => setShowHtml(true)}
            className={showHtml ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
          >
            HTML
          </button>
          <button
            onClick={() => setShowHtml(false)}
            className={!showHtml ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
          >
            Reiner Text
          </button>
          {showHtml && !showImages && m.html?.includes('src=') && (
            <button onClick={() => setShowImages(true)} className="btn-secondary text-xs">
              🖼 Bilder anzeigen
            </button>
          )}
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        {showHtml && hasHtml ? (
          <iframe
            ref={iframeRef}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            className="w-full min-h-[400px] border-0"
            title="Mail-Inhalt"
          />
        ) : (
          <pre className="p-4 text-sm whitespace-pre-wrap font-sans text-slate-800">
            {m.text ?? '(kein Inhalt)'}
          </pre>
        )}
      </div>

      {m.attachments.length > 0 && (
        <div className="card mt-4">
          <h2 className="text-sm font-semibold mb-2">📎 Anhänge ({m.attachments.length})</h2>
          <ul className="space-y-1 text-sm">
            {m.attachments.map((a) => (
              <li
                key={a.index}
                className="flex items-center gap-2 bg-slate-50 rounded px-2 py-1"
              >
                <span>📄</span>
                <span className="truncate flex-1" title={a.filename}>
                  {a.filename}
                </span>
                <span className="text-xs text-slate-400">
                  {Math.round(a.size / 1024)} KB
                </span>
                <a
                  href={`/api/v1/accounts/${accountId}/mailboxes/${encodeURIComponent(mailboxPath)}/messages/${uid}/attachments/${a.index}`}
                  download={a.filename}
                  className="text-rla-700 hover:underline text-xs"
                >
                  ⬇ Download
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
